import { motion, useScroll, useTransform } from "framer-motion";
import {
    ListChecks,
    Layers,
    CalendarClock,
    Award,
    Users,
    ArrowRight,
    Sparkles,
    AlarmClock,
    TicketPercent,
} from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { trackClickCTA } from "../lib/pixel";
import { PRESALE_DEADLINE_LABEL } from "../config/site";
import { Countdown } from "./Countdown";
import { PromoCode } from "./PromoCode";
import { usePresaleCountdown } from "../lib/presale";

const FACTS = [
    { icon: ListChecks, label: "100 konkretnych sytuacji i rozwiązań" },
    { icon: Layers, label: "5 tematycznych modułów" },
    { icon: CalendarClock, label: "Dostęp online przez 10 miesięcy" },
    { icon: Award, label: "Imienny certyfikat PDF" },
    { icon: Users, label: "Zajęcia indywidualne, grupowe i firmowe" },
];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden py-1">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const { scrollY } = useScroll();
    const yPhoto = useTransform(scrollY, [0, 700], [0, -30]);
    const { ended, lastDay, promoActive, promoTime } = usePresaleCountdown();

    return (
        <section
            id="start"
            data-testid="hero-section"
            className="relative overflow-hidden pt-[136px] sm:pt-[124px]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
                <div className="lg:col-span-7 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="inline-flex items-center gap-2 rounded-full bg-hl-lav px-4 py-2"
                    >
                        <Sparkles className="w-4 h-4 text-ink" />
                        <span className="text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold text-ink">
                            Praktyczny kurs online dla nauczycieli języków pracujących z dorosłymi
                        </span>
                    </motion.div>

                    <h1 className="font-display uppercase mt-8 text-[1.9rem] sm:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.06] text-ink">
                        <MaskedLine delay={0.25}>
                            <span className="inline-block bg-tang px-3 sm:px-5 rounded-xl -rotate-1">
                                100 rozwiązań
                            </span>
                        </MaskedLine>
                        <MaskedLine delay={0.38}>
                            <span className="inline-block bg-hl-pink px-3 sm:px-5 rounded-xl rotate-[0.6deg]">
                                na trudne sytuacje
                            </span>
                        </MaskedLine>
                        <MaskedLine delay={0.51}>
                            <span className="inline-block bg-hl-cyan px-3 sm:px-5 rounded-xl -rotate-[0.4deg]">
                                na lekcji z dorosłymi
                            </span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-7 max-w-xl text-base md:text-lg text-ink/75 font-medium leading-relaxed"
                        data-testid="hero-lead"
                    >
                        Co powiedzieć, kiedy uczeń milczy, dominuje rozmowę, traci motywację albo po
                        raz kolejny mówi: „Nie wiem”? W tym kursie znajdziesz konkretne reakcje,
                        strategie i pomysły, które możesz wykorzystać już na najbliższej lekcji.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4"
                    >
                        <button
                            data-testid="hero-primary-cta"
                            onClick={() => {
                                trackClickCTA("hero");
                                scrollToId("#pakiety");
                            }}
                            className="inline-flex items-center gap-2 rounded-full bg-tang hover:bg-[#EFE238] text-ink font-bold uppercase tracking-wide px-8 min-h-[52px] text-sm transition-all duration-300 hover:-translate-y-0.5 shadow-md"
                        >
                            Chcę mieć gotowe rozwiązania
                            <ArrowRight className="w-5 h-5" />
                        </button>
                        <button
                            data-testid="hero-secondary-cta"
                            onClick={() => {
                                trackClickCTA("hero-program");
                                scrollToId("#program");
                            }}
                            className="inline-flex items-center gap-2 text-ink font-bold uppercase tracking-wide text-sm min-h-[44px] border-b-2 border-ink/20 hover:border-ink transition-colors duration-300 pb-1"
                        >
                            Zobacz pełny program
                        </button>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 1.1 }}
                        className="mt-9 flex flex-wrap gap-3"
                        data-testid="hero-facts"
                    >
                        {FACTS.map((f) => (
                            <li
                                key={f.label}
                                className="flex items-center gap-2.5 rounded-full bg-white border border-border px-4 py-2.5 text-base font-semibold text-ink shadow-soft"
                            >
                                <f.icon className="w-5 h-5 shrink-0 text-ink" />
                                {f.label}
                            </li>
                        ))}
                    </motion.ul>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1.25 }}
                        className="mt-8 w-fit flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl bg-presale text-white px-6 py-5 shadow-lg wiggle"
                        data-testid="hero-presale-deadline"
                    >
                        {ended ? (
                            <>
                                <span className="font-display font-extrabold uppercase tracking-wide text-base sm:text-lg leading-tight">
                                    Early Bird zakończony
                                </span>
                                <span className="text-sm font-semibold text-white/85">
                                    Aktualne ceny znajdziesz w pakietach poniżej
                                </span>
                            </>
                        ) : promoActive ? (
                            <>
                                <div className="flex items-center gap-3">
                                    <TicketPercent
                                        className="w-9 h-9 text-tang shrink-0 animate-pulse"
                                        aria-hidden="true"
                                    />
                                    <span className="font-display font-extrabold uppercase tracking-wide leading-tight">
                                        <span className="block text-lg sm:text-2xl">
                                            {promoTime.lastDay
                                                ? "Tylko dziś!"
                                                : "Tylko 27 października"}
                                        </span>
                                        <span className="block text-tang text-3xl sm:text-5xl">
                                            Kod <PromoCode /> = -50 zł
                                            {promoTime.lastDay && " do północy"}
                                        </span>
                                    </span>
                                </div>
                                <Countdown variant="blocks" target="promo" />
                            </>
                        ) : (
                            <>
                                <div className="flex items-center gap-3">
                                    <AlarmClock
                                        className={`w-7 h-7 text-tang shrink-0 ${
                                            lastDay ? "animate-pulse" : ""
                                        }`}
                                        aria-hidden="true"
                                    />
                                    <span className="font-display font-extrabold uppercase tracking-wide text-base sm:text-lg leading-tight">
                                        {lastDay
                                            ? "Ostatni dzień Early Bird!"
                                            : "Early Bird kończy się"}
                                        <span className="block text-tang">
                                            {lastDay
                                                ? "Koniec dziś o północy"
                                                : PRESALE_DEADLINE_LABEL}
                                        </span>
                                    </span>
                                </div>
                                <Countdown variant="blocks" />
                            </>
                        )}
                    </motion.div>
                </div>

                <div className="lg:col-span-5 relative">
                    <div className="absolute -top-6 -right-4 sm:-right-8 w-36 h-36 bg-hl-cyan rounded-[2.5rem] rotate-6" />
                    <div className="absolute -bottom-8 -left-4 sm:-left-8 w-44 h-44 bg-hl-pink rounded-full" />
                    <motion.div
                        style={{ y: yPhoto }}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="relative rounded-[2.5rem] overflow-hidden shadow-card bg-white border border-border"
                    >
                        <img
                            src="/images/new1.webp"
                            alt="Anna Popławska i Luiza Wójtowicz-Waga - trenerki i twórczynie EduNation"
                            className="w-full h-[440px] sm:h-[540px] object-cover object-top"
                            loading="eager"
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 1.2, type: "spring" }}
                        className="absolute -bottom-6 left-4 sm:-left-6 flex items-center gap-4 rounded-2xl bg-white px-5 py-4 shadow-card border border-border"
                        data-testid="hero-trainers-caption"
                    >
                        <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-hl-pink text-ink shrink-0">
                            <Users className="w-5 h-5" />
                        </span>
                        <div>
                            <p className="font-display font-bold text-sm leading-tight text-ink">
                                Anna Popławska · Luiza Wójtowicz-Waga
                            </p>
                            <p className="text-[11px] font-semibold text-ink-mute uppercase tracking-wider mt-1">
                                Trenerki i twórczynie EduNation
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
